// components/PaymentDetails.js
import React, { useEffect, useState, useMemo } from 'react';
import { TextField, Divider} from '@mui/material';
import styled from 'styled-components';
import CampoMonetario from '../components/CampoMonetario';
import CreditoDetalhes from '../NewReceita/comp/CreditoDetalhes';
import NumeroIdentificador from '../NewReceita/comp/NumeroIdentificador';
import BancoEmissor from '../NewReceita/comp/BancoEmissor';
import { getFormasPagamento, getContasBancario } from '../../api';

import {
  Label,
  BaseLabel,
  RowContainer,
  InputSmall,
  Select,
  Checkbox,
  AutoCompleteSmall,
  InputCode,
  RowContainerTable,
  Input,
  cores,
} from '../../../styles/styleUtils';

const ParcelasContainer = styled.div`
  margin-top: 20px;
  border: 1px solid ${cores.cinzaClaro};
  border-radius: 8px;
  padding: 16px;
  background-color: ${cores.branco};
  gap: 9px; /* Aumentei o gap para melhor espaçamento, já que o cálculo da largura dos itens leva isso em conta */
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between; // Distribui o espaço igualmente entre os itens
  align-items: center;
  padding: 12px 16px;
  background-color: ${cores.laranja}; // Um fundo para destacar a barra de resumo
  border-radius: 6px;
  margin: -15px 0px 10px; // Espaçamento acima e abaixo
  flex-wrap: wrap; // Permite que os itens quebrem para a linha de baixo em telas pequenas
  gap: 16px; // Espaçamento entre os itens quando quebram a linha
`;

const SummaryItem = styled.div`
  font-size: 0.9rem;
  color: ${cores.textoClaro};

  strong {
    color: ${cores.textoClaro};
  }
`;

function formatDateToInputValue(date) {
  const localDate = new Date(date);
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset()); // Corrige fuso
  return localDate.toISOString().split('T')[0];
}

const formatCurrency = (value) => {
  // Se o valor for nulo, indefinido ou não for um número, retorna R$ 0,00
  if (typeof value !== 'number') {
    return (0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const numeroTaxa = (valor) => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  if (typeof valor === 'string') {
    const numero = Number(valor.replace(',', '.').trim());
    return Number.isFinite(numero) ? numero : 0;
  }
  return 0;
};

const obterTaxaAntecipacao = (operadora, quantidadeParcelas) => {
  const quantidade = Number(quantidadeParcelas || 0);
  const taxas = operadora?.taxasAntecipacao;

  if (!taxas || quantidade <= 0) return 0;

  // Formato mais comum no cadastro atual: array em que o índice 0 corresponde à Parcela 1.
  if (Array.isArray(taxas)) {
    const itemDireto = taxas[quantidade - 1];

    if (typeof itemDireto === 'number' || typeof itemDireto === 'string') {
      return numeroTaxa(itemDireto);
    }

    if (itemDireto && typeof itemDireto === 'object') {
      return numeroTaxa(
        itemDireto.taxa ??
        itemDireto.valor ??
        itemDireto.percentual ??
        itemDireto.porcentagem
      );
    }

    // Compatibilidade com arrays de objetos que informam explicitamente a parcela.
    const itemEncontrado = taxas.find((item) => {
      if (!item || typeof item !== 'object') return false;
      const parcela =
        item.parcela ??
        item.numeroParcela ??
        item.quantidadeParcelas ??
        item.quantidade ??
        item.id;
      return Number(parcela) === quantidade;
    });

    if (itemEncontrado) {
      return numeroTaxa(
        itemEncontrado.taxa ??
        itemEncontrado.valor ??
        itemEncontrado.percentual ??
        itemEncontrado.porcentagem
      );
    }
  }

  // Compatibilidade futura caso taxasAntecipacao passe a ser um objeto por número de parcelas.
  if (typeof taxas === 'object') {
    return numeroTaxa(taxas[quantidade] ?? taxas[String(quantidade)]);
  }

  return 0;
};

function PaymentDetails({ formData, onInputChange, onAutoCompleteChange, setFormData }) {
  const [formas, setFormas] = useState([]);
  const [parcelas, setParcelas] = useState([]);
  const [contas, setContas] = useState([]);
  const [validationMessage, setValidationMessage] = useState('');

  // por conta das formas que mudaram
  const selectedFormaPagamento = useMemo(() => {
    return formas.find((f) => f._id === formData.formaPagamento?._id) || null;
  }, [formas, formData.formaPagamento]);

  const ehCredito = Boolean(formData.formaPagamento?.nome?.includes('Crédito'));
  const ehCreditoParcelado = Boolean(formData.formaPagamento?.nome?.includes('Parcelado'));

  const taxaCartaoAplicada = useMemo(() => {
    if (!ehCredito || !formData.operadora) return 0;

    if (!ehCreditoParcelado) {
      return numeroTaxa(formData.operadora.txCreditoAVista);
    }

    if (formData.antecipacaoAutomatica === 'sim') {
      return obterTaxaAntecipacao(formData.operadora, formData.quantidadeParcelas);
    }

    return numeroTaxa(formData.operadora.txCreditoParcelado);
  }, [
    ehCredito,
    ehCreditoParcelado,
    formData.operadora,
    formData.antecipacaoAutomatica,
    formData.quantidadeParcelas,
  ]);

  const aplicarEncargosCartao = (parcelasBase) => {
    const lista = (parcelasBase || []).map((item) => ({ ...item }));
    if (!ehCredito || !formData.operadora || taxaCartaoAplicada <= 0 || lista.length === 0) {
      return lista.map((item) => ({
        ...item,
        encargos: 0,
        valorLiquido: Number(item.valor || 0),
        taxaAplicada: 0,
        antecipacao: false,
        operadora: formData.operadora || null,
        idOperadora: formData.operadora?._id || null,
      }));
    }

    const totalBrutoCentavos = lista.reduce(
      (acc, item) => acc + Math.round((Number(item.valor || 0) + Number.EPSILON) * 100),
      0
    );
    const encargoTotalCentavos = Math.round(totalBrutoCentavos * (taxaCartaoAplicada / 100));

    let encargosDistribuidos = 0;

    return lista.map((item, index) => {
      const valorCentavos = Math.round((Number(item.valor || 0) + Number.EPSILON) * 100);
      const ehUltima = index === lista.length - 1;
      const encargoCentavos = ehUltima
        ? encargoTotalCentavos - encargosDistribuidos
        : Math.round(valorCentavos * (taxaCartaoAplicada / 100));

      encargosDistribuidos += encargoCentavos;

      return {
        ...item,
        encargos: encargoCentavos / 100,
        valorLiquido: (valorCentavos - encargoCentavos) / 100,
        taxaAplicada: taxaCartaoAplicada,
        antecipacao: formData.antecipacaoAutomatica === 'sim',
        operadora: formData.operadora,
        idOperadora: formData.operadora?._id || null,
      };
    });
  };

  const fetchContas = async () => {
    const result = await getContasBancario();
    setContas(result);
  };
  useEffect(() => {
    fetchContas();
  }, []);

  useEffect(() => {
    const quantidadeValida =
      Number(formData.quantidadeParcelas || 0) > 0;
    const dataValida = Boolean(formData.dataPrimeiraParcela);
    const totalValido = Number(formData.valor || 0) > 0;

    if (quantidadeValida && dataValida && totalValido) {
      gerarParcelas();
    } else {
      setParcelas([]);

      // Só limpa o financeiro quando realmente faltam dados estruturais.
      // Não depende mais de valorParcelas, pois esse campo é calculado por
      // outro useEffect e poderia ficar vazio por um render intermediário.
      setFormData((prev) => ({
        ...prev,
        financeiro: [],
      }));
    }
  }, [
    formData.quantidadeParcelas,
    formData.periodicidade,
    formData.valorParcelas,
    formData.dataPrimeiraParcela,
    formData.valorPrimeiraParcela,
    formData.valor,
    formData.formaPagamento,
    formData.operadora,
    formData.antecipacaoAutomatica,
    taxaCartaoAplicada,
    setFormData,
  ]);

  useEffect(() => {
    fetchFormas();
  }, []);

  const fetchFormas = async () => {
    const result = await getFormasPagamento();
    setFormas(result);
  };

  useEffect(() => {
    const totalVenda = parseFloat(formData.valor || 0);
    const somaParcelas = parcelas.reduce((acc, parcela) => acc + (parseFloat(parcela.valor) || 0), 0);

    // Usa uma pequena tolerância para evitar problemas de ponto flutuante
    if (parcelas.length > 0 && Math.abs(somaParcelas - totalVenda) > 0.01) {
      setValidationMessage(
        `Valor das parcelas (R$ ${somaParcelas
          .toFixed(2)
          .replace('.', ',')}) diferente do valor total da venda (R$ ${totalVenda.toFixed(2).replace('.', ',')})`
      );
    } else {
      setValidationMessage('');
    }
  }, [parcelas, formData.valor]);

  const calcularProximaData = (dataAnterior, periodicidade) => {
    const data = new Date(dataAnterior);
    switch (periodicidade) {
      case 'quinzenal':
        data.setDate(data.getDate() + 15);
        break;
      case 'mensal':
        data.setMonth(data.getMonth() + 1);
        break;
      case 'bimestral':
        data.setMonth(data.getMonth() + 2);
        break;
      default:
        break;
    }
    return new Date(data);
  };

  const formatDateYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /*
  const gerarParcelas = () => {
    const numParcelas = parseInt(formData.quantidadeParcelas, 10);
    const valor = parseFloat(formData.valorParcelas);
    const primeiraData = new Date(formData.dataPrimeiraParcela);
    const periodo = formData.periodicidade;
    const novasParcelas = [];

    if (Number.isNaN(numParcelas) || Number.isNaN(valor) || Number.isNaN(primeiraData.getTime())) {
      setParcelas([]);
      setFormData((prev) => ({ ...prev, financeiro: [] }));
      return;
    }

    let dataAnterior = primeiraData;

    for (let i = 0; i < numParcelas; i += 1) {
      const data = i === 0 ? dataAnterior : calcularProximaData(dataAnterior, periodo);
      novasParcelas.push({
        data: new Date(data),
        valor,
        parcela: i + 1,
      });
      dataAnterior = new Date(data); // Atualiza a data anterior para a próxima iteração
    }

    const parcelasComEncargos = aplicarEncargosCartao(novasParcelas);
    const totalEncargos = parcelasComEncargos.reduce((acc, item) => acc + Number(item.encargos || 0), 0);
    const totalLiquido = parcelasComEncargos.reduce((acc, item) => acc + Number(item.valorLiquido || 0), 0);

    setParcelas(parcelasComEncargos);
    setFormData((prev) => ({
      ...prev,
      taxaCartaoAplicada,
      encargos: parseFloat(totalEncargos.toFixed(2)),
      valorLiquido: parseFloat(totalLiquido.toFixed(2)),
      financeiro: parcelasComEncargos.map((p) => ({
        ...p,
        data: formatDateYYYYMMDD(p.data),
      })),
    }));
  };
  */
  const gerarParcelas = () => {
    if (formData.visualiza) {
      setParcelas(formData.financeiro);
      return;
    }
    const numParcelas = parseInt(formData.quantidadeParcelas, 10);
    const valorTotalVenda = parseFloat(formData.valor || 0);
    const valorPrimeiraParcelaInput = parseFloat(formData.valorPrimeiraParcela || 0); // O valor do input

    const primeiraData = new Date(formData.dataPrimeiraParcela);
    const periodo = formData.periodicidade;
    const novasParcelas = [];

    // Validações iniciais
    if (
      Number.isNaN(numParcelas) ||
      numParcelas <= 0 ||
      Number.isNaN(valorTotalVenda) ||
      Number.isNaN(primeiraData.getTime())
    ) {
      setParcelas([]);
      setFormData((prev) => ({ ...prev, financeiro: [] }));
      return;
    }

    // Define se a primeira parcela terá um valor específico ou será dividida igualmente
    const usarValorEspecificoPrimeiraParcela =
      valorPrimeiraParcelaInput > 0 && !Number.isNaN(valorPrimeiraParcelaInput);

    let valorPrimeiraParcelaReal = 0;
    let valorParcelasRestantes = 0;

    if (usarValorEspecificoPrimeiraParcela) {
      valorPrimeiraParcelaReal = valorPrimeiraParcelaInput;
      const valorRestanteParaDividir = valorTotalVenda - valorPrimeiraParcelaReal;
      const numParcelasRestantes = numParcelas - 1;

      if (numParcelasRestantes > 0) {
        valorParcelasRestantes = valorRestanteParaDividir / numParcelasRestantes;
      } else {
        // Se tiver apenas 1 parcela e valorPrimeiraParcela for usado, essa parcela é o valor total
        valorPrimeiraParcelaReal = valorTotalVenda;
      }
    } else {
      // Se valorPrimeiraParcela for 0 ou vazio, divide o valor total igualmente entre todas as parcelas
      valorPrimeiraParcelaReal = valorTotalVenda / numParcelas;
      valorParcelasRestantes = valorPrimeiraParcelaReal; // Todas as parcelas terão o mesmo valor
    }

    let dataAnterior = primeiraData;

    for (let i = 0; i < numParcelas; i += 1) {
      const data = i === 0 ? dataAnterior : calcularProximaData(dataAnterior, periodo);
      const valorDaParcela = i === 0 ? valorPrimeiraParcelaReal : valorParcelasRestantes;

      novasParcelas.push({
        data: new Date(data),
        valor: parseFloat(valorDaParcela.toFixed(2)),
        parcela: i + 1,
        formaPagamento: formData.formaPagamento, // Garante que a forma de pagamento padrão seja aplicada
      });
      dataAnterior = new Date(data);
    }

    // Ajuste final para garantir que o somatório das parcelas seja igual ao valor total da venda
    const somaAtualParcelas = novasParcelas.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    const diferenca = valorTotalVenda - somaAtualParcelas;

    if (Math.abs(diferenca) > 0.01 && novasParcelas.length > 0) {
      // Adiciona a diferença (positiva ou negativa) à última parcela
      novasParcelas[novasParcelas.length - 1].valor = parseFloat(
        (novasParcelas[novasParcelas.length - 1].valor + diferenca).toFixed(2)
      );
    }

    const parcelasComEncargos = aplicarEncargosCartao(novasParcelas);
    const totalEncargos = parcelasComEncargos.reduce(
      (acc, item) => acc + Number(item.encargos || 0),
      0
    );
    const totalLiquido = parcelasComEncargos.reduce(
      (acc, item) => acc + Number(item.valorLiquido ?? item.valor ?? 0),
      0
    );

    setParcelas(parcelasComEncargos);
    setFormData((prev) => ({
      ...prev,
      taxaCartaoAplicada,
      encargos: parseFloat(totalEncargos.toFixed(2)),
      valorLiquido: parseFloat(totalLiquido.toFixed(2)),
      financeiro: parcelasComEncargos.map((p) => ({
        ...p,
        data: formatDateYYYYMMDD(p.data),
      })),
    }));
  };

  const handleTableCellChange = (index, field, value) => {
    let newValue = value;
    if (field === 'data') {
      newValue = new Date(`${value}T00:00:00`);
    }
    const novasParcelas = [...parcelas];
    novasParcelas[index][field] = newValue;
    const parcelasAtualizadas = field === 'valor'
      ? aplicarEncargosCartao(novasParcelas)
      : novasParcelas;
    const totalEncargos = parcelasAtualizadas.reduce((acc, item) => acc + Number(item.encargos || 0), 0);
    const totalLiquido = parcelasAtualizadas.reduce((acc, item) => acc + Number(item.valorLiquido ?? item.valor ?? 0), 0);

    setParcelas(parcelasAtualizadas);
    setFormData((prev) => ({
      ...prev,
      taxaCartaoAplicada,
      encargos: parseFloat(totalEncargos.toFixed(2)),
      valorLiquido: parseFloat(totalLiquido.toFixed(2)),
      financeiro: parcelasAtualizadas.map((p) => ({ ...p })),
    }));
  };

  const changePagamentoParcela = (index, field, value) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index][field] = value;
    setParcelas(novasParcelas);
    setFormData((prev) => ({ ...prev, financeiro: novasParcelas.map((p) => ({ ...p })) }));
  };

  useEffect(() => {
    if (!formData.dataRecebimento) {
      setFormData((prev) => {
        const newReceita = { ...prev };
        delete newReceita.tipoRecebimento;
        delete newReceita.valorBaixa;
        delete newReceita.desconto;
        delete newReceita.acrescimo;
        delete newReceita.contaRecebimento;
        delete newReceita.idContaRecebimento;
        delete newReceita.bancoEmissor;
        delete newReceita.idBancoEmissor;
        delete newReceita.NumeroIdentificador;
        return newReceita;
      });
    }
  }, [formData.dataRecebimento, setFormData]);

  return (
    <div>
      <SummaryContainer>
        <SummaryItem>
          <strong>Valor Kit: {formatCurrency(formData.valorKit)}</strong>
        </SummaryItem>
        <SummaryItem>
          <strong>Valor Serviços: {formatCurrency(formData.valorServico)}</strong>
        </SummaryItem>
        <SummaryItem>
          <strong>Valor Seguro:{formatCurrency(formData.valorSeguro)} </strong> 
        </SummaryItem>
        <SummaryItem>
          <strong>Valor Total: {formatCurrency(formData.valor)} </strong>
        </SummaryItem>
      </SummaryContainer>
      <Divider />
      <RowContainer>
        <BaseLabel htmlFor="formaPagamento">Forma de Pagamento</BaseLabel>
        <AutoCompleteSmall
          disablePortal
          id="formaPagamento"
          name="formaPagamento"
          value={selectedFormaPagamento}
          onChange={(_, value) => onAutoCompleteChange('idFormaPagamento', 'formaPagamento', value)}
          options={formas}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => `${option.nome}`}
          renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          disabled={formData.visualiza}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="quantidadeParcelas">Quantidade de Parcelas</BaseLabel>
        <InputSmall
          type="number"
          id="quantidadeParcelas"
          name="quantidadeParcelas"
          value={formData.quantidadeParcelas}
          onChange={onInputChange}
          disabled={formData.visualiza}
        />
        <span />
        <span />
        <span />
        <span />
        {formData.quantidadeParcelas > 1 && <Label htmlFor="periodicidade">Periodicidade</Label>}
        {formData.quantidadeParcelas > 1 && (
          <Select
            id="periodicidade"
            name="periodicidade"
            value={formData.periodicidade}
            onChange={onInputChange}
            disabled={formData.visualiza}
          >
            <option value="">Selecione</option>
            <option value="quinzenal">Quinzenal</option>
            <option value="mensal">Mensal</option>
            <option value="bimestral">Bimestral</option>
          </Select>
        )}
      </RowContainer>

      <RowContainer>
        <BaseLabel htmlFor="valorParcelas">Valor das Parcelas</BaseLabel>
        <CampoMonetario name={'valorParcelas'} valor={formData.valorParcelas} onChange={onInputChange} disabled />
        <span />
        <span />
        <Checkbox type="checkbox" id="manual" name="manual" checked={formData.manual} onChange={onInputChange} />
        <Label htmlFor="manual">Manual</Label>
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="dataPrimeiraParcela">Data Primeira Parcela</BaseLabel>
        <InputSmall
          type="date"
          id="dataPrimeiraParcela"
          name="dataPrimeiraParcela"
          value={formatDateToInputValue(formData.dataPrimeiraParcela)}
          onChange={onInputChange}
          disabled={formData.visualiza}
        />
        <Label htmlFor="valorParcelas">Valor Primeira Parcela</Label>
        <CampoMonetario
          name={'valorPrimeiraParcela'}
          valor={formData.valorPrimeiraParcela || 0}
          onChange={onInputChange}
          disabled={formData.visualiza || formData.quantidadeParcelas <= 1}
        />
        <Label htmlFor="dataRecebimento">Data Recebimento</Label>
        <InputSmall
          type="date"
          id="dataRecebimento"
          name="dataRecebimento"
          value={formData.dataRecebimento ? formatDateToInputValue(formData.dataRecebimento) : ''}
          onChange={onInputChange}
          disabled={formData.visualiza}
        />
      </RowContainer>
      {formData.dataRecebimento && (
        <>
          <RowContainer>
            {formData.encargos && !ehCredito && (
              <>
                <Label htmlFor="encargos">Encargos</Label>
                <CampoMonetario name={'encargos'} valor={formData.encargos || 0} onChange={onInputChange} disabled />
                <Label htmlFor="valorLiquido">Líquido</Label>
                <CampoMonetario
                  name={'valorLiquido'}
                  valor={formData.valorLiquido || 0}
                  onChange={onInputChange}
                  disabled
                />
              </>
            )}
          </RowContainer>
          <RowContainer>
            <BaseLabel htmlFor="contaRecebimento">Conta Recebimento</BaseLabel>
            <AutoCompleteSmall
              disablePortal
              id="contaRecebimento"
              name="contaRecebimento"
              value={formData.contaRecebimento || null}
              onChange={(_, value) => onAutoCompleteChange('idContaRecebimento', 'contaRecebimento', value)}
              options={contas}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => `${option.nome}`}
              renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
            />
          </RowContainer>
        </>
      )}
      {formData.formaPagamento && formData.formaPagamento.nome.includes('Crédito') && (
        <>
          <CreditoDetalhes
            onAutoCompleteChange={onAutoCompleteChange}
            setFormData={setFormData}
            formData={formData}
          />

          {formData.operadora && (
            <RowContainer>
              <BaseLabel htmlFor="taxaCartaoAplicada">Taxa aplicada</BaseLabel>
              <InputSmall
                id="taxaCartaoAplicada"
                name="taxaCartaoAplicada"
                value={`${taxaCartaoAplicada.toFixed(2).replace('.', ',')}%`}
                disabled
              />
              <Label htmlFor="encargos">Encargos previstos</Label>
              <CampoMonetario
                name={'encargos'}
                valor={Number(formData.encargos || 0)}
                onChange={onInputChange}
                disabled
              />
              <Label htmlFor="valorLiquido">Líquido previsto</Label>
              <CampoMonetario
                name={'valorLiquido'}
                valor={Number(formData.valorLiquido || formData.valor || 0)}
                onChange={onInputChange}
                disabled
              />
            </RowContainer>
          )}
        </>
      )}

      {formData.dataRecebimento &&
        formData.formaPagamento &&
        ['Financiamento', 'Cheque', 'Boleto'].some((keyword) => formData.formaPagamento.nome.includes(keyword)) && (
          <NumeroIdentificador onInputChange={onInputChange} formData={formData} />
        )}
      {formData.dataRecebimento && formData.formaPagamento && formData.formaPagamento.nome.includes('Boleto') && (
        <BancoEmissor onAutoCompleteChange={onAutoCompleteChange} formData={formData} onInputChange={onInputChange} />
      )}
      {/* Adicione o alerta aqui, antes da ParcelasContainer ou onde preferir */}
      {validationMessage && (
        <p style={{ color: 'yellow', fontWeight: 'bold', marginTop: '10px' }}>{validationMessage}</p>
      )}
      {formData.dataRecebimento && (
        <p style={{ color: 'yellow', fontWeight: 'bold', marginTop: '10px' }}>
          Todas as parcelas serão baixadas, caso queira baixar somente algumas parcelas deixe sem Data de Recebimento e
          faça a baixa pela tela de Receitas
        </p>
      )}
      {parcelas.length > 0 && formData.manual && (
        <ParcelasContainer>
          {parcelas.map((parcela, index) => {
            return (
              <React.Fragment key={`parcela-${index}`}>
                <RowContainerTable>
                  <InputCode
                    style={{ width: 40 }}
                    type="number"
                    id="valorParcelas"
                    name="valorParcelas"
                    value={parcela.parcela}
                    onChange={(e) => handleTableCellChange(index, 'valor', parseFloat(e.target.value))}
                    disabled
                  />
                  <Input
                    style={{ width: 150 }}
                    type="date"
                    id="parcelaData"
                    name="parcelaData"
                    value={formatDateToInputValue(parcela.data)}
                    onChange={(e) =>
                      // handleTableCellChange(parcelaIndex, 'data', format(new Date(e.target.value), 'dd/MM/yyyy'))
                      handleTableCellChange(index, 'data', e.target.value)
                    }
                    disabled={formData.visualiza}
                  />
                  <AutoCompleteSmall
                    disablePortal
                    style={{ width: '50%' }}
                    id="formaPagamento"
                    name="formaPagamento"
                    value={parcela.formaPagamento || selectedFormaPagamento}
                    onChange={(_, value) => changePagamentoParcela(index, 'formaPagamento', value)}
                    options={formas}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    getOptionLabel={(option) => `${option.nome}`}
                    renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                    disabled={formData.visualiza}
                  />
                  <CampoMonetario
                    name={'valorParcelas'}
                    valor={parcela.valor}
                    onChange={(e) => handleTableCellChange(index, 'valor', parseFloat(e.target.value))}
                    disabled={formData.visualiza}
                  />
                </RowContainerTable>
              </React.Fragment>
            );
          })}
        </ParcelasContainer>
      )}
    </div>
  );
}

export default PaymentDetails;